import {
  createSafeStorage,
  createStrictContext,
  fromTryCatch,
  type StorageBackend,
} from "@indietabletop/appkit";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { array, number, object, string, type Infer } from "superstruct";
import type { Product } from "../client.ts";

const CART_KEY = "cart";

function cartItem() {
  return object({
    id: string(),
    quantity: number(),
  });
}

type Cart = Infer<ReturnType<typeof cart>>;

/**
 * The persisted shape of the cart.
 *
 * Only product IDs and quantities are stored — names, prices, and ownership
 * are always re-resolved from the shop endpoint at render time, so a cart
 * that has been sitting in storage for a month can never show a stale price.
 *
 * `updatedTs` is not used by anything yet, but is what a future sync endpoint
 * would need to resolve conflicts between devices.
 */
function cart() {
  return object({
    items: array(cartItem()),
    updatedTs: number(),
  });
}

function createCartStorage(backend?: StorageBackend) {
  return createSafeStorage({ [CART_KEY]: cart() }, backend);
}

const EMPTY_CART: Cart = { items: [], updatedTs: 0 };

function readCart(storage: ReturnType<typeof createCartStorage>): Cart {
  // Note the `??`: safe storage returns `undefined` (not `null`) when the
  // stored value fails validation, so an explicit null check would let a
  // malformed cart through.
  return storage.getItem(CART_KEY) ?? EMPTY_CART;
}

function writeCart(storage: ReturnType<typeof createCartStorage>, next: Cart) {
  const result = fromTryCatch(() => {
    storage.setItem(CART_KEY, next);
  });

  if (result.isFailure) {
    console.warn("Could not persist cart.", result.failure);
  }
}

/**
 * A product in the cart, resolved against the fetched product list and
 * carrying its cart quantity.
 */
export type CartProduct = Product & { quantity: number };

type CartContextValue = {
  /**
   * The cart's items resolved against the product list passed to
   * `CartProvider`, dropping any product IDs that no longer exist in the shop
   * or that the user has since come to own.
   */
  products: CartProduct[];
  total: number;
  has: (id: string) => boolean;
  add: (id: string) => void;
  remove: (id: string) => void;
  clear: () => void;
};

const [CartContext, useCartContext] =
  createStrictContext<CartContextValue>("Cart Context");

export { useCartContext };

type CartProviderProps = {
  children: ReactNode;

  /**
   * The full, freshly-fetched product list, so cart products can be derived
   * from cart state plus this list without a separate fetch or hook.
   */
  products: Product[];

  /**
   * Defaults to `localStorage`. An in-memory {@link StorageBackend} is useful
   * in tests and Storybook, where runs should stay isolated from each other.
   */
  backend?: StorageBackend;
};

/**
 * Provides a localStorage-backed shopping cart to `useCartContext`.
 *
 * The cart is read from storage once on mount, and an effect writes it back
 * after every subsequent change to state. A separate `storage` listener keeps
 * it in sync with writes made by other tabs — the `storage` event never fires
 * in the tab that made the write, which is why mutations below update state
 * directly instead of relying on it. Everything about the cart — its
 * persisted shape, the storage instance, and the raw item list — stays inside
 * this provider; consumers only ever see resolved `CartProduct`s.
 */
export function CartProvider(props: CartProviderProps) {
  const [storage] = useState(() => createCartStorage(props.backend));
  const [cart, setCart] = useState<Cart>(() => readCart(storage));

  // Persists on every change, including the initial one — a redundant write
  // of the value that was just read is harmless, and it keeps this the only
  // place that writes to storage.
  useEffect(() => {
    writeCart(storage, cart);
  }, [cart, storage]);

  useEffect(() => {
    const controller = new AbortController();

    window.addEventListener(
      "storage",
      (event) => {
        // A null key means the whole storage area was cleared.
        if (event.key !== CART_KEY && event.key !== null) {
          return;
        }

        setCart(readCart(storage));
      },
      { signal: controller.signal },
    );

    return () => {
      controller.abort();
    };
  }, [storage]);

  const value = useMemo<CartContextValue>(() => {
    const byId = new Map(
      props.products.map((product) => [product.id, product]),
    );

    const products = cart.items.flatMap((item) => {
      const product = byId.get(item.id);

      return product && !product.ownership
        ? [{ ...product, quantity: item.quantity }]
        : [];
    });

    return {
      products,

      get total() {
        return products.reduce((sum, p) => sum + p.price, 0);
      },

      has(id) {
        return cart.items.some((item) => item.id === id);
      },

      // Adding a product that is already in the cart is a no-op — Greathelm
      // sells digital products, so there is never a reason to hold two of the
      // same item.
      add(id) {
        setCart((current) => {
          if (current.items.some((item) => item.id === id)) {
            return current;
          }

          return {
            items: [...current.items, { id, quantity: 1 }],
            updatedTs: Date.now(),
          };
        });
      },

      remove(id) {
        setCart((current) => {
          if (!current.items.some((item) => item.id === id)) {
            return current;
          }

          return {
            items: current.items.filter((item) => item.id !== id),
            updatedTs: Date.now(),
          };
        });
      },

      clear() {
        setCart((current) => {
          if (!current.items.length) {
            return current;
          }

          return { items: [], updatedTs: Date.now() };
        });
      },
    };
  }, [cart, props.products]);

  return (
    <CartContext.Provider value={value}>{props.children}</CartContext.Provider>
  );
}
